import constant from '@/utils/constant'
import cache from '@/utils/cache'
import { useAppStore } from '@/store/modules/app'
import { sysStore } from '@/store/modules/sys'
import { Base64 } from 'js-base64'
import dayjs from 'dayjs'

// 把路径转换成驼峰命名
export const pathToCamel = (path: string): string => {
	return path.replace(/\/(\w)/g, (all, letter) => letter.toUpperCase())
}

// 是否外链
export const isExternalLink = (url: string): boolean => {
	return /^(https?:|\/\/|http?:|\/\/|^{{\s?apiUrl\s?}})/.test(url)
}

// 替换外链参数
export const replaceLinkParam = (url: string): string => {
	return url.replace('{{apiUrl}}', constant.apiUrl)
}

// 转换文件大小格式
export const convertSizeFormat = (size: number): string => {
	const unit = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
	if (size === 0) {
		return '0 Bytes'
	}

	let index = Math.floor(Math.log(size) / Math.log(1024))
	if (index < 0) {
		index = 0
	} // 确保索引不为负数
	if (index >= unit.length) {
		index = unit.length - 1
	} // 确保索引在范围内

	let newSize = size / Math.pow(1024, index)
	return newSize.toFixed(2) + ' ' + unit[index]
}

// 生成uuid
export const generateUUID = () => {
	let uuid = ''
	for (let i = 0; i < 32; i++) {
		let random = (Math.random() * 16) | 0
		if (i === 8 || i === 12 || i === 16 || i === 20) {
			uuid += '-'
		}
		uuid += (i === 12 ? 4 : i === 16 ? (random & 3) | 8 : random).toString(16)
	}
	return uuid
}

// 获取svg图标(id)列表
export const getIconSvgList = (): string[] => {
	const rs: string[] = []
	const list1 = document.querySelectorAll('iconfont')
	const list = document.querySelectorAll('svg symbol[id^="icon-"]')
	for (let i = 0; i < list.length; i++) {
		rs.push(list[i].id)
	}
	return rs
}
// 获取图标 根据类名列表
export const getIconClassList = (): string[] => {
	const rs: string[] = []
	const list = document.querySelectorAll('iconfont')

	for (let i = 0; i < list.length; i++) {
		rs.push(list[i].id)
	}
	return rs
}
// 获取字典Label
export const getDictLabel = (dictList: any[], dictType: string, dictValue: string) => {
	const type = dictList.find((element: any) => element.dictType === dictType)
	if (type) {
		const val = type.dataList.find((element: any) => element.dictValue === dictValue + '')
		if (val) {
			return val.dictLabel
		} else {
			return dictValue
		}
	} else {
		return dictValue
	}
}

// 获取字典Label样式
export const getDictLabelClass = (dictList: any[], dictType: string, dictValue: string): string => {
	const type = dictList.find((element: any) => element.dictType === dictType)
	if (type) {
		const val = type.dataList.find((element: any) => element.dictValue === dictValue + '')
		if (val) {
			return val.labelClass
		} else {
			return ''
		}
	} else {
		return ''
	}
}

export const getDictLabelList = (dictType: string, dictValue: any): string => {
	if (Number.isInteger(dictValue)) {
		dictValue = dictValue + ''
	}

	if (!dictValue) {
		return ''
	}

	const appStore = useAppStore()

	let result = ''
	dictValue.split(',').forEach((value: string) => {
		const classStyle = getDictLabelClass(appStore.dictList, dictType, value) || 'primary'
		const label = getDictLabel(appStore.dictList, dictType, value)
		if (classStyle) {
			result += `<span class="el-tag el-tag--${classStyle} el-tag--${appStore.componentSize}">${label}</span>&nbsp;`
		} else {
			result += label + '&nbsp;'
		}
	})

	return result
}

// 获取字典数据列表
export const getDictDataList = (dictList: any[], dictType: string) => {
	const type = dictList.find((element: any) => element.dictType === dictType)
	if (type) {
		return type.dataList
	} else {
		return []
	}
}

//通过类型名获取 字典列表
export const getDictlistByDictType = (dictType: string) => {
	try {
		const appStore = useAppStore()
		const dictItem: any = appStore.dictList.find((e: any) => e.dictType === dictType)
		return dictItem.dataList
	} catch (error) {
		return []
	}
}
// 树形数据转换
export const treeDataTranslate = (data: any[], id?: string, pid?: string): any[] => {
	const res: any[] = []
	const temp: any = {}
	id = id || 'id'
	pid = pid || 'pid'
	for (let i = 0; i < data.length; i++) {
		temp[data[i][id]] = data[i]
	}
	for (let k = 0; k < data.length; k++) {
		if (!temp[data[k][pid]] || data[k][id] === data[k][pid]) {
			res.push(data[k])
			continue
		}
		if (!temp[data[k][pid]]['children']) {
			temp[data[k][pid]]['children'] = []
		}
		temp[data[k][pid]]['children'].push(data[k])
	}
	return res
}

// 生成数字字母混合字符串
export const getRandom = (num: number) => {
	const chars = [
		'0',
		'1',
		'2',
		'3',
		'4',
		'5',
		'6',
		'7',
		'8',
		'9',
		'a',
		'b',
		'c',
		'd',
		'e',
		'f',
		'g',
		'h',
		'i',
		'j',
		'k',
		'l',
		'm',
		'n',
		'o',
		'p',
		'q',
		'r',
		's',
		't',
		'u',
		'v',
		'w',
		'x',
		'y',
		'z'
	]
	let res = ''
	for (let i = 0; i < num; i++) {
		const id = Math.floor(Math.random() * 36)
		res += chars[id]
	}
	return res
}

type Primitive = string | number | boolean | bigint | symbol | null | undefined

// 深克隆
export const deepClone = (obj: any): any => {
	if (obj === null || typeof obj !== 'object') {
		return obj
	}

	let clone: any = Array.isArray(obj) ? [] : {}

	for (let key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			clone[key] = deepClone(obj[key])
		}
	}

	return clone
}

export const defaultUpload = () => {
	return constant.uploadUrl + '?access_token=' + cache.getToken()
}
export const setUploadExcelUrl = (url: string) => {
	return constant.apiUrl + `${url}?access_token=` + cache.getToken()
}
// 文件预览
export const handlePreview = (url: string) => {
	const viewURL = `${constant.previewUrl}?url=${Base64.encode(constant.apiUrl + url)}`
	window.open(viewURL)
}

export const getFileUrl = (url: string) => {
	return `${constant.previewUrl}?url=${Base64.encode(constant.apiUrl + url)}`
}

// 格式日期
export const formatDateTime = (dateUrl: string) => {
	try {
		const dateTime = dayjs(dateUrl)
		const today = dayjs().format('YYYY-MM-DD')
		const displayTime = dateTime.format('YYYY-MM-DD') === today ? dateTime.format('HH:mm:ss') : dateUrl
		return displayTime
	} catch (error) {
		return dateUrl
	}
}

export const getFileTypeIcon = (filetype: string): string => {
	const dict: { [key: string]: string } = {
		docx: 'icon-docx',
		doc: 'icon-doc',
		pdf: 'icon-PDF',
		txt: 'icon-txt',
		xls: 'icon-xls',
		xlsx: 'icon-xlsx',
		ppt: 'icon-ppt',
		jpg: 'icon-jpg',
		md: 'icon-MD',
		mp4: 'icon-mp4',
		png: 'icon-png',
		mp3: 'icon-mp3'
	}
	const defaultIcon = 'icon-unknownfile'
	return filetype in dict ? dict[filetype] : defaultIcon
}

// 获取特征值或 波形标签
export const getWaveOrEigTags = (arrString: any, type_: string, keyType = 'id') => {
	if (!arrString) {
		return []
	}
	const storeSys = sysStore()
	let { sys_wave, sys_eig } = storeSys
	let arr = type_ == 'eig' ? sys_eig : sys_wave
	let keys: any[] = []
	if (typeof arrString === 'string') {
		keys = arrString.split(',')
	} else if (Array.isArray(arrString)) {
		keys = arrString
	}
	return arr.filter((item: any) => keys.includes(item[keyType]))
}

export const filterByType = (arr: any, ty: any) => {
	let result: any = []

	arr.forEach((item: any) => {
		if (item.type === ty) {
			result.push(item)
		}

		if (item.children && item.children.length > 0) {
			const childResults = filterByType(item.children, ty)
			result = result.concat(childResults) // 合并子节点的结果
		}
	})
	return result
}

export const removeSessionKey = (str: any) => {
	// 获取所有的 sessionStorage 键
	const keys = Object.keys(sessionStorage)

	// 遍历所有键，检查并删除包含指定字符串的项
	keys.forEach(key => {
		if (key.includes(str)) {
			sessionStorage.removeItem(key)
		}
	})
}

//设置实时数据
export const setRealData = (eigData: any, typeName: any) => {
	const result: any = {}
	const storeSys = sysStore()
	let { sys_eig } = storeSys
	let op: any = typeName.includes('三相电流')
		? {
				A: '',
				B: '',
				C: ''
			}
		: {
				V: '垂直',
				A: '轴向',
				H: '水平',
				O: ''
			}
	// 临时存储温度和位移数据
	const tempData: any = {}

	for (const key in eigData) {
		if (eigData[key] === null || eigData[key] === undefined) {
			continue
		}

		let time = dayjs(Number(eigData[key]['ts']) * 1000).format('YYYY/MM/DD HH:mm:ss')
		result[`时间`] = time
		sys_eig.forEach((eig: any) => {
			const nickname = eig.nickname
			if (eigData[key][nickname] !== undefined) {
				// 温度特征值先存储，稍后处理
				if (eig.name === '温度') {
					if (!tempData[eig.name]) {
						tempData[eig.name] = eigData[key][nickname] + ' ' + eig.unit
					}
				} else {
					const needPrefix = ['速度', '加速度', '加速度峰值', '加速度峰峰值', '位移'].includes(eig.name)
					const labelKey = needPrefix ? `${op[key]}${eig.name}` : eig.name
					result[labelKey] = eigData[key][nickname] + ' ' + eig.unit
				}
			}
		})
	}
	// 最后添加温度
	Object.keys(tempData).forEach(key => {
		result[key] = tempData[key]
	})

	return result
}
export const setWarnTime = (predTime: any, n: number) => {
	const currentTime = dayjs(predTime)

	const beforeNTimestamp = currentTime.subtract(n, 'hour').valueOf()

	const afterNTimestamp = currentTime.valueOf()

	return [beforeNTimestamp, afterNTimestamp]
}

export const mergeArrays = (a: any, b: any) => {
	const merged = [...a]

	// 遍历数组 b
	b.forEach((itemB: any) => {
		const existingItem = merged.find(itemA => itemA.key === itemB.key)

		if (existingItem) {
			existingItem.value = itemB.value
		} else {
			merged.push(itemB)
		}
	})

	return merged
}

export const findFormFieldByName = (fieldName: string, targetWidgetForm?: any) => {
	const searchForm = targetWidgetForm
	for (const column of searchForm.list[0].columns) {
		for (const field of column.list) {
			if (field.name === fieldName) {
				return { column, field }
			}
		}
	}
	return null
}
// export const getStyleByCss = (name: string) => {
// 	return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
// }
